// @ts-strict-ignore
import moment from 'moment';

import type { FeedItemType } from '@/types';
import { getAppTitle } from '@/utils/getAppTitle';
import { getChatHeader } from '@/utils/getChatHeader';
import { getStringFromHTML } from '@/utils/getStringFromHTML';
import { getKeys } from '@/utils/object';
import { getTitle as getPublicSourceTitle } from '@/utils/public-source-utils/getTitle';

const OVERRIDE_MAP = {
  comments: 'origin_category',
} as const;

export const getSnipData = (
  result: FeedItemType,
  paragraphNumber = 1
): ReturnType => {
  const source = result.source;
  const sourceLabel = getAppTitle(result.source);
  const deeplink = result.deeplink;

  const highlights =
    result.highlights && result.highlights.length > 0 && paragraphNumber > 0
      ? getStringFromHTML(result.highlights[paragraphNumber - 1])
      : undefined;

  let key = 'category';
  if (result.category in OVERRIDE_MAP) {
    key = OVERRIDE_MAP[result.category];
  }

  if (!(key in result)) {
    throw new Error(
      `Key ${key} not found in result. Keys: ${getKeys(result).join(', ')}`
    );
  }

  switch (result[key as keyof FeedItemType]) {
    case 'bse_annual_reports':
    case 'bse_conf_calls':
    case 'bse_presentations':
    case 'bse_filings':
    case 'bse_sdd_sast':
    case 'earnings_conf_calls':
    case 'nzx_filings':
      return {
        source,
        sourceLabel,
        title: result.company_name || '',
        body: highlights || result.plain_text || result.newssub || '',
        deeplink,
        metadata: {
          Category: result.secondary_category?.join(', '),
          'Security Id': result.security_id,
          'Security Code': result.security_code,
          'Report Date': moment.unix(result.unix).format('DD-MM-YYYY'),
        },
      };
    case 'crisil':
    case 'regulators':
      return {
        source,
        sourceLabel,
        title: result.company_name || getPublicSourceTitle(result),
        body:
          highlights ||
          (Array.isArray(result.link) ? result.link.join(' ') : result.link) ||
          '',
        deeplink,
      };
    case 'chats':
      return {
        source,
        sourceLabel,
        title: getChatHeader(result),
        body:
          highlights || result.plain_text || result.generalized_mime_type || '',
        contacts_for_people_filter: result.contacts_for_people_filter,
        contact: result.group_name || result.sender_name,
        deeplink,
      };
    case 'gmail':
      return {
        source,
        sourceLabel,
        title: result.subject || '(no subject)',
        body:
          highlights ||
          result.plain_text ||
          getStringFromHTML(result.body || ''),
        deeplink,
        metadata: {
          From: result.origin_email_ua || result.origin_email,
          To: result.sender_mail_id,
        },
        contact: result.origin_email,
        contacts_for_people_filter: result.contacts_for_people_filter,
      };
    case 'socialnetwork': {
      return {
        source,
        sourceLabel,
        title:
          result.name ||
          result.user ||
          (Array.isArray(result.link) ? result.link[0] : result.link) ||
          '',
        body:
          (result.source === 'reddit'
            ? result.title ||
              (Array.isArray(result.link) ? result.link.join(' ') : result.link)
            : result.additional_content ||
              result.content ||
              result.embedded_link) || '',
        deeplink,
        contact: result.source === 'twitter' ? `@${result.user}` : '',
        contacts_for_people_filter:
          result.source === 'twitter'
            ? result.contacts_for_people_filter
            : [''],
      };
    }
    case 'notes':
      return {
        source,
        sourceLabel,
        title: result.note_title || result.notebook_title || '',
        body: highlights || result.plain_text || '',
        deeplink,
      };
    case 'webclips':
    case 'drives':
      return {
        source,
        sourceLabel,
        sources: result.sources,
        title: result.original_filename?.[0] || result.title || '',
        body: highlights || '',
        deeplink,
      };
    case 'documents':
    case 'clipped_reports':
    case 'media':
      return {
        source,
        sources: result.sources,
        sourceLabel: `${getAppTitle(
          result.retained_sources?.[0] ?? ''
        )} ${getAppTitle(result.source)}`,
        title: result.title || '',
        body: highlights || '',
        deeplink,
      };
    case 'links':
      return {
        source: 'links',
        sourceLabel: `${getAppTitle(result.source)} Links`,
        title: getPublicSourceTitle(result),
        body:
          highlights ||
          result.plain_text ||
          (Array.isArray(result.link) ? result.link.join(' ') : result.link) ||
          '',
        deeplink,
      };
    case 'news':
    case 'blogs':
    case 'research':
    case 'forums':
    default:
      return {
        source,
        sourceLabel: result.label || '',
        title: getPublicSourceTitle(result),
        body: highlights || result.plain_text || '',
        deeplink,
        thumbnailLocation: result.thumbnail_location,
      };
  }
};

type ReturnType = {
  source: string;
  sources?: string[];
  sourceLabel: string;
  title: string;
  body: string;
  deeplink: string;
  metadata?: Record<string, string | undefined>;
  thumbnailLocation?: string;
  contact?: string;
  contacts_for_people_filter?: string[];
};
